import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { Calendar, Package, Shirt, User, Clock, Loader2, Eye, RefreshCw } from 'lucide-react';
import DataTable from '@/components/DataTable';
import Pagination from '@/components/Pagination';
import DetalleSolicitudModal from '@/components/solicitudes/DetalleSolicitudModal';

const ITEMS_PER_PAGE = 5; // Menos items para el widget

const SolicitudesDelDiaWidget = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('lenceria');
  
  const [isLoadingInsumos, setIsLoadingInsumos] = useState(false);
  const [isLoadingLenceria, setIsLoadingLenceria] = useState(false);
  const [isLoadingLenceriaVertical, setIsLoadingLenceriaVertical] = useState(false);

  // Estados para modal de detalle
  const [selectedSolicitud, setSelectedSolicitud] = useState(null);
  const [isDetalleModalOpen, setIsDetalleModalOpen] = useState(false);
  const [tipoSolicitudDetalle, setTipoSolicitudDetalle] = useState('insumos');

  // Estados para solicitudes
  const [solicitudesInsumos, setSolicitudesInsumos] = useState([]);
  const [solicitudesLenceria, setSolicitudesLenceria] = useState([]);
  const [solicitudesLenceriaVertical, setSolicitudesLenceriaVertical] = useState([]);

  // Estados para paginación
  const [currentPageInsumos, setCurrentPageInsumos] = useState(1);
  const [currentPageLenceria, setCurrentPageLenceria] = useState(1);
  const [currentPageLenceriaVertical, setCurrentPageLenceriaVertical] = useState(1);

  // Estados para totales
  const [totalInsumos, setTotalInsumos] = useState(0);
  const [totalLenceria, setTotalLenceria] = useState(0);
  const [totalLenceriaVertical, setTotalLenceriaVertical] = useState(0);
  const [isLoadingTotales, setIsLoadingTotales] = useState(false);

  // Estados para totales de pendientes
  const [pendientesInsumos, setPendientesInsumos] = useState(0);
  const [pendientesLenceria, setPendientesLenceria] = useState(0);
  const [pendientesLenceriaVertical, setPendientesLenceriaVertical] = useState(0);

  // Obtener fecha de hoy
  const today = new Date().toISOString().split('T')[0];

  // Función para obtener rango de fechas del día
  const getDateRange = () => {
    const baseDate = new Date(today + 'T00:00:00');
    
    const inicioDelDia = new Date(baseDate);
    inicioDelDia.setHours(0, 0, 0, 0);

    const finDelDia = new Date(baseDate);
    finDelDia.setHours(23, 59, 59, 999);

    return { inicioDelDia, finDelDia };
  };

  // Función para cargar totales de todas las solicitudes
  const fetchTotales = useCallback(async () => {
    setIsLoadingTotales(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();
      const query = {
        fechaEntrega: {
          $gte: inicioDelDia.toISOString(),
          $lte: finDelDia.toISOString()
        },
        estado: { $ne: 'Eliminado' }
      };

      // Query para pendientes
      const queryPendientes = {
        ...query,
        estado: 'Pendiente'
      };

      // Cargar totales y pendientes en paralelo
      const [
        insumosRes, 
        lenceriaRes, 
        lenceriaVerticalRes,
        insumosPendientesRes,
        lenceriaPendientesRes,
        lenceriaVerticalPendientesRes
      ] = await Promise.all([
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query })
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query })
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query })
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query: queryPendientes })
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query: queryPendientes })
        }),
        fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/filter`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 1, offset: 1, query: queryPendientes })
        })
      ]);

      const [
        insumosData, 
        lenceriaData, 
        lenceriaVerticalData,
        insumosPendientesData,
        lenceriaPendientesData,
        lenceriaVerticalPendientesData
      ] = await Promise.all([
        insumosRes.json(),
        lenceriaRes.json(),
        lenceriaVerticalRes.json(),
        insumosPendientesRes.json(),
        lenceriaPendientesRes.json(),
        lenceriaVerticalPendientesRes.json()
      ]);

      const newTotalInsumos = Number(insumosData.totalRecords || insumosData.total || 0);
      const newTotalLenceria = Number(lenceriaData.totalRecords || lenceriaData.total || 0);
      const newTotalLenceriaVertical = Number(lenceriaVerticalData.totalRecords || lenceriaVerticalData.total || 0);

      const newPendientesInsumos = Number(insumosPendientesData.totalRecords || insumosPendientesData.total || 0);
      const newPendientesLenceria = Number(lenceriaPendientesData.totalRecords || lenceriaPendientesData.total || 0);
      const newPendientesLenceriaVertical = Number(lenceriaVerticalPendientesData.totalRecords || lenceriaVerticalPendientesData.total || 0);


      // Detectar nuevas solicitudes y notificar
      const totalAnterior = totalInsumos + totalLenceria + totalLenceriaVertical;
      const totalNuevo = newTotalInsumos + newTotalLenceria + newTotalLenceriaVertical;

      if (totalAnterior > 0 && totalNuevo > totalAnterior) {
        const diferencia = totalNuevo - totalAnterior;
        toast({
          title: '🔔 Nueva(s) Solicitud(es)',
          description: `Se ${diferencia === 1 ? 'ha detectado 1 nueva solicitud' : `han detectado ${diferencia} nuevas solicitudes`} para hoy`,
          duration: 5000,
          className: 'bg-gradient-to-r from-blue-500 to-purple-600 text-white border-none',
          style: {
            background: 'linear-gradient(to right, #3b82f6, #9333ea)',
            color: 'white',
            border: 'none',
            boxShadow: '0 10px 25px rgba(59, 130, 246, 0.3)'
          }
        });

        // Recargar el tab activo para mostrar las nuevas solicitudes
        if (activeTab === 'insumos') {
          fetchSolicitudesInsumos();
        } else if (activeTab === 'lenceria') {
          fetchSolicitudesLenceria();
        } else if (activeTab === 'lenceria-vertical') {
          fetchSolicitudesLenceriaVertical();
        }
      }

      // Asegurar que los valores sean números antes de establecerlos
      setTotalInsumos(Number(newTotalInsumos) || 0);
      setTotalLenceria(Number(newTotalLenceria) || 0);
      setTotalLenceriaVertical(Number(newTotalLenceriaVertical) || 0);
      setPendientesInsumos(Number(newPendientesInsumos) || 0);
      setPendientesLenceria(Number(newPendientesLenceria) || 0);
      setPendientesLenceriaVertical(Number(newPendientesLenceriaVertical) || 0);

    } catch (error) {
      console.error('Error al cargar totales:', error);
    } finally {
      setIsLoadingTotales(false);
    }
  }, [toast, totalInsumos, totalLenceria, totalLenceriaVertical, activeTab]);

  // Fetch solicitudes de insumos
  const fetchSolicitudesInsumos = useCallback(async () => {
    setIsLoadingInsumos(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-insumos/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPageInsumos,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' }
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Error al obtener solicitudes de insumos');
      }

      const data = await response.json();
      setSolicitudesInsumos(data.data || []);
    } catch (error) {
      console.error('Error al cargar solicitudes de insumos:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes de insumos',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingInsumos(false);
    }
  }, [toast, currentPageInsumos]);

  // Fetch solicitudes de lencería
  const fetchSolicitudesLenceria = useCallback(async () => {
    setIsLoadingLenceria(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPageLenceria,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' }
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Error al obtener solicitudes de lencería');
      }

      const data = await response.json();
      setSolicitudesLenceria(data.data || []);
    } catch (error) {
      console.error('Error al cargar solicitudes de lencería:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes de lencería',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingLenceria(false);
    }
  }, [toast, currentPageLenceria]);

  // Fetch solicitudes de lencería vertical
  const fetchSolicitudesLenceriaVertical = useCallback(async () => {
    setIsLoadingLenceriaVertical(true);
    try {
      const { inicioDelDia, finDelDia } = getDateRange();

      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:3000'}/solicitud-prendas-vertical/filter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          limit: ITEMS_PER_PAGE,
          offset: currentPageLenceriaVertical,
          query: {
            fechaEntrega: {
              $gte: inicioDelDia.toISOString(),
              $lte: finDelDia.toISOString()
            },
            estado: { $ne: 'Eliminado' }
          }
        }),
      });

      if (!response.ok) {
        throw new Error('Error al obtener solicitudes de lencería vertical');
      }

      const data = await response.json();
      setSolicitudesLenceriaVertical(data.data || []);
    } catch (error) {
      console.error('Error al cargar solicitudes de lencería vertical:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las solicitudes de lencería vertical',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingLenceriaVertical(false);
    }
  }, [toast, currentPageLenceriaVertical]);

  // Cargar totales al montar el componente
  useEffect(() => {
    fetchTotales();
  }, []);

  // Polling para detectar nuevas solicitudes cada 30 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTotales();
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [fetchTotales]);

  // Cargar datos al montar y cuando cambia el tab
  useEffect(() => {
    if (activeTab === 'insumos') {
      fetchSolicitudesInsumos();
    } else if (activeTab === 'lenceria') {
      fetchSolicitudesLenceria();
    } else if (activeTab === 'lenceria-vertical') {
      fetchSolicitudesLenceriaVertical();
    }
  }, [activeTab, fetchSolicitudesInsumos, fetchSolicitudesLenceria, fetchSolicitudesLenceriaVertical]);

  // Recargar datos cuando cambia la página de insumos
  useEffect(() => {
    if (activeTab === 'insumos') {
      fetchSolicitudesInsumos();
    }
  }, [currentPageInsumos]);

  // Recargar datos cuando cambia la página de lencería
  useEffect(() => {
    if (activeTab === 'lenceria') {
      fetchSolicitudesLenceria();
    }
  }, [currentPageLenceria]);

  // Recargar datos cuando cambia la página de lencería vertical
  useEffect(() => {
    if (activeTab === 'lenceria-vertical') {
      fetchSolicitudesLenceriaVertical();
    }
  }, [currentPageLenceriaVertical]);

  // Handlers para acciones
  const handleVerDetalle = (solicitud, tipo) => {
    setSelectedSolicitud(solicitud);
    setTipoSolicitudDetalle(tipo);
    setIsDetalleModalOpen(true);
  };

  const handleRefresh = () => {
    fetchTotales();
    // Resetear a página 1 al actualizar
    setCurrentPageInsumos(1);
    setCurrentPageLenceria(1);
    setCurrentPageLenceriaVertical(1);
    if (activeTab === 'insumos') {
      fetchSolicitudesInsumos();
    } else if (activeTab === 'lenceria') {
      fetchSolicitudesLenceria();
    } else if (activeTab === 'lenceria-vertical') {
      fetchSolicitudesLenceriaVertical();
    }
  };

  // Columnas para tabla de insumos
  const columnsInsumos = [
  {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'edificio',
      label: 'Propiedad',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm">{row.edificio?.nombre}</p>
          {row.hospedaje && (
            <p className="text-xs text-blue-600 font-medium">📍 {row.hospedaje.nombre}</p>
          )}
          {row.nombrePropiedad && !row.hospedaje && (
            <p className="text-xs text-blue-600 font-medium">📍 {row.nombrePropiedad}</p>
          )}
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs">
          {row.total_solicitado} unidades
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-orange-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={`${colors[row.estado] || 'bg-gray-100 text-gray-800'} text-xs`}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-insumos',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50'
        }
      ]
    }
  ];

  // Columnas para tabla de lencería
  const columnsLenceria = [
    {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'fechaEntrega',
      label: 'Fecha Entrega',
      sortable: true,
      render: (row) => (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span className="text-sm">
            {new Date(row.fechaEntrega).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric'
            })}
          </span>
        </div>
      )
    },
    {
      id: 'edificio',
      label: 'Edificio',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm">{row.edificio?.nombre}</p>
          {row.nombrePropiedad && (
            <p className="text-xs text-blue-600 font-medium">📍 {row.nombrePropiedad}</p>
          )}
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
          {row.total_solicitado}
        </Badge>
      )
    },
    {
      id:'peso_total',
      label: 'Total Peso',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
          {row.peso_total/1000} kg
        </Badge>
      )
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm">{row.creator?.nombre}</p>
    
        </div>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-orange-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={`${colors[row.estado] || 'bg-gray-100 text-gray-800'} text-xs`}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50'
        }
      ]
    }
  ];

  // Columnas para tabla de lencería vertical
  const columnsLenceriaVertical = [
    {
      id: 'createdAt',
      label: 'fecha de solicitud',
      sortable: true,
      render: (row) => new Date(row.createdAt).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
    {
      id: 'edificio',
      label: 'Edificio / dirección',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm">{row.edificio?.nombre}</p>
          {row.edificio?.direccion && (
            <p className="text-xs text-purple-600 font-medium">📍 {row.edificio?.direccion}</p>
          )}
        </div>
      )
    },
    {
      id: 'solicitante',
      label: 'Solicitante',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-sm">{row.creator?.nombre}</p>
    
        </div>
      )
    },
    {
      id: 'total_solicitado',
      label: 'Total',
      sortable: true,
      render: (row) => (
        <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
          {row.total_solicitado} prendas
        </Badge>
      )
    },
    {
      id: 'estado',
      label: 'Estado',
      sortable: true,
      render: (row) => {
        const colors = {
          'Pendiente': 'bg-yellow-100 text-yellow-800',
          'Alistado': 'bg-blue-100 text-blue-800',
          'En Camino': 'bg-orange-100 text-orange-800',
          'Entregado': 'bg-green-100 text-green-800',
          'Cancelado': 'bg-red-100 text-red-800'
        };
        return (
          <Badge className={`${colors[row.estado] || 'bg-gray-100 text-gray-800'} text-xs`}>
            {row.estado}
          </Badge>
        );
      }
    },
    {
      id: 'actions',
      label: 'Acciones',
      actions: [
        {
          key: 'ver-detalle-lenceria-vertical',
          icon: 'Eye',
          tooltip: 'Ver Detalle',
          className: 'text-blue-600 hover:bg-blue-50'
        }
      ]
    }
  ];

  // Handler para acciones de la tabla
  const handleAction = (actionKey, row) => {
    if (actionKey === 'ver-detalle-insumos') {
      handleVerDetalle(row, 'insumos');
    } else if (actionKey === 'ver-detalle-lenceria') {
      handleVerDetalle(row, 'lenceria');
    } else if (actionKey === 'ver-detalle-lenceria-vertical') {
      handleVerDetalle(row, 'lenceria-vertical');
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-blue-600" />
                Solicitudes del Día
              </CardTitle>
              <CardDescription>
                Solicitudes programadas para hoy - {new Date().toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </CardDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isLoadingInsumos || isLoadingLenceria || isLoadingLenceriaVertical}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${(isLoadingInsumos || isLoadingLenceria || isLoadingLenceriaVertical) ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 h-auto p-1 bg-gray-100">
              <TabsTrigger 
                value="lenceria" 
                className="flex items-center justify-between gap-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:border-blue-500 data-[state=inactive]:border data-[state=inactive]:border-gray-300 data-[state=inactive]:hover:bg-gray-200 data-[state=inactive]:hover:border-gray-400 transition-all py-3 px-4 cursor-pointer rounded-md"
              >
                <div className="flex items-center gap-2">
                  <Shirt className="h-5 w-5 text-blue-600" />
                  <span className="font-semibold text-sm">Lencería Horizontal</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm">
                    {totalLenceria}
                  </Badge>
                  {pendientesLenceria > 0 && (
                    <Badge className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm animate-pulse">
                      {pendientesLenceria}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="lenceria-vertical" 
                className="flex items-center justify-between gap-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:border-purple-500 data-[state=inactive]:border data-[state=inactive]:border-gray-300 data-[state=inactive]:hover:bg-gray-200 data-[state=inactive]:hover:border-gray-400 transition-all py-3 px-4 cursor-pointer rounded-md"
              >
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-purple-600" />
                  <span className="font-semibold text-sm">Lencería Vertical</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className="bg-purple-500 hover:bg-purple-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm">
                    {totalLenceriaVertical}
                  </Badge>
                  {pendientesLenceriaVertical > 0 && (
                    <Badge className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm animate-pulse">
                      {pendientesLenceriaVertical}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="insumos" 
                className="flex items-center justify-between gap-3 data-[state=active]:bg-white data-[state=active]:shadow-md data-[state=active]:border-green-500 data-[state=inactive]:border data-[state=inactive]:border-gray-300 data-[state=inactive]:hover:bg-gray-200 data-[state=inactive]:hover:border-gray-400 transition-all py-3 px-4 cursor-pointer rounded-md"
              >
                <div className="flex items-center gap-2">
                  <Package className="h-5 w-5 text-green-600" />
                  <span className="font-semibold text-sm">Insumos</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Badge className="bg-green-500 hover:bg-green-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm">
                    {totalInsumos}
                  </Badge>
                  {pendientesInsumos > 0 && (
                    <Badge className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-2 py-0.5 shadow-sm animate-pulse">
                      {pendientesInsumos}
                    </Badge>
                  )}
                </div>
              </TabsTrigger>
            </TabsList>

            {/* Tab de Lencería */}
            <TabsContent value="lenceria" className="mt-4">
              {isLoadingLenceria ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : solicitudesLenceria.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Shirt className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No hay solicitudes de lencería para hoy</p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsLenceria}
                    data={solicitudesLenceria}
                    onAction={handleAction}
                  />
                  {totalLenceria > ITEMS_PER_PAGE && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={currentPageLenceria}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCurrentPageLenceria}
                        totalItems={totalLenceria}
                      />
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Tab de Lencería Vertical */}
            <TabsContent value="lenceria-vertical" className="mt-4">
              {isLoadingLenceriaVertical ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : solicitudesLenceriaVertical.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No hay solicitudes de lencería vertical para hoy</p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsLenceriaVertical}
                    data={solicitudesLenceriaVertical}
                    onAction={handleAction}
                  />
                  {totalLenceriaVertical > ITEMS_PER_PAGE && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={currentPageLenceriaVertical}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCurrentPageLenceriaVertical}
                        totalItems={totalLenceriaVertical}
                      />
                    </div>
                  )}
                </>
              )}
            </TabsContent>

            {/* Tab de Insumos */}
            <TabsContent value="insumos" className="mt-4">
              {isLoadingInsumos ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                </div>
              ) : solicitudesInsumos.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Package className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No hay solicitudes de insumos para hoy</p>
                </div>
              ) : (
                <>
                  <DataTable
                    columns={columnsInsumos}
                    data={solicitudesInsumos}
                    onAction={handleAction}
                  />
                  {totalInsumos > ITEMS_PER_PAGE && (
                    <div className="mt-4">
                      <Pagination
                        currentPage={currentPageInsumos}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={setCurrentPageInsumos}
                        totalItems={totalInsumos}
                      />
                    </div>
                  )}
                </>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Modal de detalle */}
      <DetalleSolicitudModal
        isOpen={isDetalleModalOpen}
        onClose={() => setIsDetalleModalOpen(false)}
        solicitud={selectedSolicitud}
        tipo={tipoSolicitudDetalle}
      />
    </>
  );
};

export default SolicitudesDelDiaWidget;
